/**
 * StepperShell — 14-stage vertical stepper wrapping the Master Build Spec pipeline.
 *
 * Runs ONLY when `stepper_pipeline_enabled=true` for the current tenant.
 * All UI colours, typography and spacing follow Master Spec §Part 2:
 *   - Deep charcoal background (#0a0b0e), not pure black
 *   - Orange accent used sparingly (primary action + active stage only)
 *   - Sans-serif for interface; JetBrains Mono / tabular-nums for data
 *   - Generous whitespace, large KPI numerals, one stage on-screen at a time
 *
 * Stage content is delegated to the existing module components — this
 * file owns the SHELL only (top bar, stepper rail, gating, transitions).
 * Session E owns wiring the remaining stages 4-14.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useParams, useNavigate, Outlet } from "react-router-dom";
import { Check, AlertCircle, Lock, ChevronRight, ChevronLeft,
  User as UserIcon, Bell, Search, PlayCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import UnifiedTopNav from "@/components/UnifiedTopNav";

const PHASES = [
  { id: "data",     label: "Data",                   stages: [1, 2] },
  { id: "modelest", label: "Modelling & Estimation", stages: [4, 5, 7] },
  { id: "planning", label: "Planning",               stages: [8, 10] },
  { id: "report",   label: "Reporting",              stages: [13] },
];

// Only these stages are implemented — the stepper hides everything else so
// the user sees a clean 1-of-8 progression rather than jumping over gaps.
const ACTIVE_STAGES = [1, 2, 4, 5, 7, 8, 10, 13];
const STAGE_ORDER = ACTIVE_STAGES;
// Map real backend stage id → the sequential number shown to the user.
const DISPLAY_NUMBER = Object.fromEntries(ACTIVE_STAGES.map((sid, idx) => [sid, idx + 1]));

const STAGE_SUBTITLE = {
  1:  "Commodity · units · coordinate system · tenement boundary",
  2:  "Collar · survey · assay · lithology import",
  4:  "Grade histograms · outlier scan · top-cut hint",
  5:  "Implicit + explicit surface modelling · ore shell",
  7:  "IDW / Kriging · LOOCV · Estimation Confidence Indicator",
  8:  "Block model · material class rules · classified 3D view",
  10: "Optimal pit shell · Lerchs–Grossmann max-flow",
  13: "One-click DOCX / PDF report · AI narrative auto-generated",
};

function StatusDot({ status }) {
  if (status === "complete") return <Check className="h-3.5 w-3.5 text-emerald-600" />;
  if (status === "needs_rerun") return <AlertCircle className="h-3.5 w-3.5 text-red-600" />;
  if (status === "in_progress") return <div className="h-2.5 w-2.5 rounded-full bg-lime-500 animate-pulse" />;
  return <div className="h-2.5 w-2.5 rounded-full border border-neutral-600" />;
}

export default function StepperShell() {
  const { id: projectId, workspace } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeStageId, setActiveStageId] = useState(1);
  const [gateEval, setGateEval] = useState(null);

  const loadState = useCallback(async () => {
    if (!projectId) return;
    try {
      // ensure any real-work already done gets hydrated into the state doc
      await api.post(`/projects/${projectId}/pipeline-state/hydrate`, {}).catch(() => {});
      const { data } = await api.get(`/projects/${projectId}/pipeline-state`);
      setState(data);
    } catch (e) {
      toast.error(`Pipeline load failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { loadState(); }, [loadState]);

  // Master demo detection — the seeded Zephyr Ridge Au-Cu project sets
  // `settings.demo_tag = 'smin_master_demo_v1'`. Expose a floating pill
  // that jumps into the workspace URL (`/w/overview`) so the guided
  // MasterDemoTour on ProjectDetailPage can auto-start.
  const [projectDoc, setProjectDoc] = useState(null);
  useEffect(() => {
    if (!projectId) return;
    api.get(`/projects/${projectId}`).then((r) => setProjectDoc(r.data)).catch(() => {});
  }, [projectId]);
  const isMasterDemo = projectDoc?.settings?.demo_tag === "smin_master_demo_v1";

  // Iter I-38 · P11 — allow any deeply-nested child (e.g. Pit KPI card in
  // MinePlanTab) to jump directly to a target stage without prop-drilling.
  useEffect(() => {
    const onJump = (e) => {
      const target = Number(e?.detail?.stageId);
      if (ACTIVE_STAGES.includes(target)) setActiveStageId(target);
    };
    window.addEventListener("smin:jump-to-stage", onJump);
    return () => window.removeEventListener("smin:jump-to-stage", onJump);
  }, []);

  useEffect(() => {
    if (!projectId || !activeStageId) return;
    api.get(`/projects/${projectId}/pipeline-state/${activeStageId}/next`)
      .then(({ data }) => setGateEval(data))
      .catch(() => setGateEval(null));
  }, [projectId, activeStageId, state]);

  const stages = useMemo(() => state?.stages || [], [state]);
  const byId = useMemo(() => Object.fromEntries(stages.map((s) => [s.stage_id, s])), [stages]);
  const active = byId[activeStageId];

  const canJumpTo = (_sid) => true;  // Iter I-27 · every stage is always reachable

  const goNext = () => {
    // Prefer backend gate-eval next_stage; fall back to next entry in ACTIVE_STAGES.
    const idx = ACTIVE_STAGES.indexOf(activeStageId);
    const next = idx >= 0 && idx < ACTIVE_STAGES.length - 1 ? ACTIVE_STAGES[idx + 1] : null;
    if (gateEval?.can_advance && ACTIVE_STAGES.includes(gateEval.next_stage)) {
      setActiveStageId(gateEval.next_stage);
    } else if (next) {
      setActiveStageId(next);
    }
  };
  const goBack = () => {
    const idx = ACTIVE_STAGES.indexOf(activeStageId);
    if (idx > 0) setActiveStageId(ACTIVE_STAGES[idx - 1]);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white text-neutral-900 grid place-items-center font-sans">
        Loading pipeline…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white text-neutral-900 font-sans"
         data-testid="stepper-shell">
      {/* ─── Minimal top bar (dark charcoal per user spec) ─────────── */}
      <header className="h-14 border-b border-neutral-800 bg-[#111418] text-neutral-100 flex items-center px-5 gap-4">
        <div className="text-[13px] font-semibold tracking-tight flex items-center gap-3"
             title="SMIN · A product of Shanvi Resources">
          <img
            src="/branding/smin-logo-horizontal.png?v=9"
            alt="SMIN — Shanvi Mining Intelligence"
            className="max-h-10 w-auto object-contain shrink-0 brightness-0 invert"
            draggable={false}
          />
          <span className="text-neutral-500 font-normal">·</span>
          <span className="text-neutral-400 font-normal">Pipeline</span>
        </div>
        <button onClick={() => navigate("/projects")}
          className="text-xs text-neutral-400 hover:text-white transition"
          data-testid="stepper-project-switcher">
          Change project
        </button>
        <div className="flex-1" />
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-neutral-500" />
          <input placeholder="Search project…"
            className="h-8 w-56 bg-neutral-800/60 border border-neutral-700 text-neutral-100 rounded pl-8 pr-3 text-[12px] placeholder:text-neutral-500 focus:outline-none focus:border-[#4CAF50]"
            data-testid="stepper-search"/>
        </div>
        <button className="text-neutral-400 hover:text-white" data-testid="stepper-notif">
          <Bell className="h-4 w-4"/>
        </button>
        <div className="pl-2 border-l border-neutral-700 flex items-center gap-2 text-[12px] text-neutral-300">
          <UserIcon className="h-3.5 w-3.5"/>
          {user?.email}
        </div>
      </header>

      <UnifiedTopNav />

      <div className="flex" style={{ minHeight: "calc(100vh - 3.5rem - 2.25rem)" }}>
        {/* ─── Left vertical stepper rail ──────────────────────────── */}
        <aside className="w-[320px] shrink-0 bg-white border-r border-neutral-200 overflow-y-auto"
               data-testid="stepper-rail">
          <div className="p-5">
            <div className="text-[10px] uppercase tracking-[0.15em] text-neutral-500 mb-1">Project</div>
            <div className="text-[15px] font-medium tracking-tight">TEST_Iso pipeline</div>
          </div>
          {PHASES.map((phase) => (
            <div key={phase.id} className="mb-2">
              <div className="px-5 py-2 text-[10px] uppercase tracking-[0.18em] text-neutral-500 border-t border-neutral-200">
                {phase.label}
              </div>
              <ul>
                {phase.stages.map((sid) => {
                  const s = byId[sid];
                  if (!s) return null;
                  const isActive = sid === activeStageId;
                  const jumpable = canJumpTo(sid);
                  return (
                    <li key={sid}>
                      <button
                        disabled={!jumpable}
                        onClick={() => setActiveStageId(sid)}
                        className={`w-full text-left flex items-start gap-3 px-5 py-2.5 border-l-2 transition
                          ${isActive
                            ? "border-[#4CAF50] bg-[#4CAF50]/10"
                            : jumpable
                              ? "border-transparent hover:bg-neutral-100"
                              : "border-transparent opacity-40 cursor-not-allowed"}
                        `}
                        data-testid={`stepper-stage-${sid}`}>
                        <div className="mt-1 shrink-0"><StatusDot status={s.status} /></div>
                        <div className="flex-1 min-w-0">
                          <div className={`text-[12.5px] font-medium truncate ${isActive ? "text-[#4CAF50]" : "text-neutral-900"}`}>
                            {DISPLAY_NUMBER[sid] || sid}. {s.name}
                          </div>
                          <div className="text-[10.5px] text-neutral-500 mt-0.5 leading-tight line-clamp-2">
                            {STAGE_SUBTITLE[sid]}
                          </div>
                          {s.status === "needs_rerun" && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[9.5px] text-red-600 border border-red-500 rounded px-1.5 py-0.5"
                                 data-testid={`stepper-badge-rerun-${sid}`}>
                              <AlertCircle className="h-2.5 w-2.5"/> Needs re-run
                            </div>
                          )}
                          {s.status === "complete" && s.output_summary && (
                            <div className="mt-1 text-[9.5px] font-mono text-emerald-600 truncate">
                              ✓ {Object.entries(s.output_summary).slice(0,1).map(([k,v])=>`${k}=${typeof v==='number'?v.toLocaleString():String(v).slice(0,24)}`)[0]}
                            </div>
                          )}
                          {s.gate === "soft" && s.status !== "complete" && (
                            <div className="mt-1 text-[9.5px] text-neutral-500">optional</div>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </aside>

        {/* ─── Main stage panel ─────────────────────────────────── */}
        <main className="flex-1 min-w-0 overflow-y-auto" data-testid="stepper-main">
          <div className="max-w-5xl mx-auto px-10 py-8">
            {/* Stage header */}
            <div className="flex items-start justify-between gap-4 mb-8">
              <div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 mb-2">
                  Stage {DISPLAY_NUMBER[activeStageId] || activeStageId} of {ACTIVE_STAGES.length}
                </div>
                <h1 className="text-3xl font-medium tracking-tight text-neutral-900">
                  {active?.name}
                </h1>
                <p className="text-[13px] text-neutral-500 mt-2 max-w-2xl">
                  {STAGE_SUBTITLE[activeStageId]}
                </p>
              </div>
              <div className="text-right">
                <StageStatusPill status={active?.status} gate={active?.gate} />
                {active?.completed_by && (
                  <div className="text-[10.5px] font-mono text-neutral-500 mt-2">
                    by {active.completed_by} · {active.completed_at?.slice(0,10)}
                  </div>
                )}
              </div>
            </div>

            {/* Soft-gate warning banner */}
            {gateEval?.soft_warnings?.length > 0 && (
              <div className="mb-6 p-3 rounded border border-amber-500 bg-amber-50 text-[12px] text-amber-800"
                   data-testid="stepper-soft-warning">
                <span className="text-amber-600 font-medium">Optional stage:</span>{" "}
                {gateEval.soft_warnings[0]}
              </div>
            )}

            {/* Stage content */}
            <div className="min-h-[400px]">
              <StageContent projectId={projectId} stageId={activeStageId} onCompleted={loadState} />
            </div>

            {/* Bottom navigation */}
            <div className="mt-10 pt-6 border-t border-neutral-200 flex items-center justify-between">
              <button
                onClick={goBack}
                disabled={ACTIVE_STAGES.indexOf(activeStageId) <= 0}
                className="text-[13px] px-4 py-2 border border-neutral-200 hover:border-neutral-400 disabled:opacity-30 disabled:cursor-not-allowed rounded flex items-center gap-2 transition"
                data-testid="stepper-back">
                <ChevronLeft className="h-4 w-4" /> Back
              </button>
              <div className="text-[11px] text-neutral-500">
                {gateEval && !gateEval.can_advance && gateEval.blocking_reason && (
                  <span className="flex items-center gap-2" data-testid="stepper-blocking-reason">
                    <Lock className="h-3 w-3 text-red-600"/>
                    {gateEval.blocking_reason}
                  </span>
                )}
              </div>
              <button
                onClick={goNext}
                disabled={ACTIVE_STAGES.indexOf(activeStageId) >= ACTIVE_STAGES.length - 1}
                className="text-[13px] px-5 py-2 bg-[#4CAF50] hover:bg-[#43A047] disabled:bg-neutral-100 disabled:text-neutral-400 disabled:cursor-not-allowed text-white font-medium rounded flex items-center gap-2 transition"
                data-testid="stepper-next">
                Next Stage <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </main>
      </div>

      {/* Floating "Take the tour" pill — surfaced on the master demo
          stepper so users landing on /projects/{id} can jump into the
          workspace URL where the guided walkthrough auto-starts. */}
      {isMasterDemo && (
        <button
          onClick={() => {
            try { window.localStorage.removeItem("smin.master_demo_tour.dismissed_v1"); } catch { /* ignore */ }
            navigate(`/projects/${projectId}/w/overview`);
          }}
          data-testid="stepper-master-demo-tour-btn"
          title="Take the guided walkthrough"
          className="fixed bottom-6 right-6 z-40 flex items-center gap-2 pl-3 pr-4 py-2
                     bg-amber-500 hover:bg-amber-600 text-white shadow-lg
                     rounded-full font-heading text-xs uppercase tracking-widest
                     transition-all hover:scale-[1.03]"
        >
          <PlayCircle className="h-4 w-4" strokeWidth={2} />
          Take the tour
        </button>
      )}
    </div>
  );
}

function StageStatusPill({ status, gate }) {
  const map = {
    complete:    { label: "Complete",     cls: "border-emerald-500 text-emerald-600 bg-emerald-50" },
    needs_rerun: { label: "Needs re-run", cls: "border-red-500 text-red-600 bg-red-50" },
    in_progress: { label: "In progress",  cls: "border-[#4CAF50]/40 text-[#4CAF50] bg-[#4CAF50]/10" },
    not_started: { label: "Not started",  cls: "border-neutral-300 text-neutral-500 bg-neutral-100" },
  };
  const m = map[status] || map.not_started;
  return (
    <div className="flex items-center gap-2 justify-end">
      <span className={`text-[10px] uppercase tracking-wider font-medium px-2.5 py-1 border rounded ${m.cls}`}>
        {m.label}
      </span>
      {gate === "soft" && <span className="text-[9.5px] text-neutral-500">soft-gate</span>}
      {gate === "strict" && <span className="text-[9.5px] text-neutral-500">strict-gate</span>}
    </div>
  );
}

/* ── Stage-content router — Sessions D + E-1 + E-2 + E-3 wire stages 1-14 ── */
import Stage1ProjectSetup from "./stages/Stage1ProjectSetup";
import Stage2DrillholeImport from "./stages/Stage2DrillholeImport";
import Stage4EDA from "./stages/Stage4EDA";
import Stage5GeologicalModel from "./stages/Stage5GeologicalModel";
import Stage7GradeEstimation from "./stages/Stage7GradeEstimation";
import Stage8BlockModelClassification from "./stages/Stage8BlockModelClassification";
import Stage10PitOptimization from "./stages/Stage10PitOptimization";
import Stage13ReportCompliance from "./stages/Stage13ReportCompliance";
import StageStub from "./stages/StageStub";

function StageContent({ projectId, stageId, onCompleted }) {
  const props = { projectId, onCompleted };
  switch (stageId) {
    case 1: return <Stage1ProjectSetup {...props} />;
    case 2: return <Stage2DrillholeImport {...props} />;
    case 4: return <Stage4EDA {...props} />;
    case 5: return <Stage5GeologicalModel {...props} />;
    case 7: return <Stage7GradeEstimation {...props} />;
    case 8: return <Stage8BlockModelClassification {...props} />;
    case 10: return <Stage10PitOptimization {...props} />;
    case 13: return <Stage13ReportCompliance {...props} />;
    default: return <StageStub stageId={stageId} />;
  }
}
